/**
 * AlertsPanel - Phase 3.1 Dashboard Enhancement
 * 
 * Shows real-time alerts with filtering by severity.
 * Supports WebSocket updates for live notifications.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  X,
  Filter,
  ChevronDown,
  ExternalLink,
  Clock,
  Loader2,
  RefreshCw
} from 'lucide-react';

type AlertSeverity = 'critical' | 'warning' | 'info' | 'success';

interface Alert {
  id: string;
  title: string;
  message: string;
  severity: AlertSeverity;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  source?: string;
  reportId?: string;
  draftId?: string;
}

interface AlertsPanelProps {
  maxAlerts?: number;
  className?: string;
  showFilter?: boolean;
  compact?: boolean;
}

const SEVERITY_CONFIG: Record<AlertSeverity, {
  icon: React.ElementType;
  bg: React.CSSProperties;
  iconColor: React.CSSProperties;
  textColor: React.CSSProperties;
  badge: React.CSSProperties;
}> = {
  critical: {
    icon: AlertCircle,
    bg: { backgroundColor: 'var(--color-blocker-tint)' },
    iconColor: { color: 'var(--color-blocker)' },
    textColor: { color: 'var(--color-blocker)' },
    badge: { backgroundColor: 'var(--color-blocker-tint)', color: 'var(--color-blocker)' }
  },
  warning: {
    icon: AlertTriangle,
    bg: { backgroundColor: 'var(--color-needs-human-tint)' },
    iconColor: { color: 'var(--color-brand)' },
    textColor: { color: 'var(--color-needs-human-text)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
  },
  info: {
    icon: Info,
    bg: { backgroundColor: 'var(--color-needs-human-tint)' },
    iconColor: { color: 'var(--color-needs-human-text)' },
    textColor: { color: 'var(--color-needs-human-text)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
  },
  success: {
    icon: CheckCircle,
    bg: { backgroundColor: 'transparent' },
    iconColor: { color: 'var(--color-verified)' },
    textColor: { color: 'var(--color-verified)' },
    badge: { backgroundColor: 'transparent', color: 'var(--color-verified)' }
  }
};

export default function AlertsPanel({ 
  maxAlerts = 10, 
  className = '',
  showFilter = true,
  compact = false
}: AlertsPanelProps) {
  const { getToken } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<AlertSeverity | 'all'>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // Set up polling for updates (will be replaced with WebSocket in Phase 5)
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/dashboard/alerts', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setAlerts((data.alerts || []).slice(0, maxAlerts));
    } catch (err) {
      console.debug('Alerts API not available, using fallback');
      // Fallback to empty - no mock alerts needed
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = async (alertId: string) => {
    try {
      const token = await getToken();
      await api.post(`/api/v1/dashboard/alerts/${alertId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      // Optimistically remove from UI
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    }
  };

  const markAsRead = async (alertId: string) => {
    try {
      const token = await getToken();
      await api.post(`/api/v1/dashboard/alerts/${alertId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, read: true } : a
      ));
    } catch (err) {
      // Optimistically mark as read
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, read: true } : a
      ));
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredAlerts = filter === 'all' 
    ? alerts 
    : alerts.filter(a => a.severity === filter);

  const unreadCount = alerts.filter(a => !a.read).length;
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;

  if (loading) {
    return (
      <div className={`rounded-xl shadow-sm border p-6 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-sm border ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
        <div className="flex items-center">
          <Bell className="w-5 h-5 mr-2" style={{ color: 'var(--color-text-secondary)' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Alerts</h3>
          {unreadCount > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
              {unreadCount} new
            </span>
          )}
          {criticalCount > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: 'var(--color-blocker-tint)', color: 'var(--color-blocker)' }}>
              {criticalCount} critical
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {showFilter && (
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="flex items-center px-3 py-1.5 text-sm rounded-lg transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <Filter className="w-4 h-4 mr-1" />
                {filter === 'all' ? 'All' : filter}
                <ChevronDown className="w-4 h-4 ml-1" />
              </button>

              {showFilterDropdown && (
                <div className="absolute right-0 mt-1 w-40 rounded-lg shadow-lg border py-1 z-10" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                  {(['all', 'critical', 'warning', 'info', 'success'] as const).map(option => (
                    <button
                      key={option}
                      onClick={() => {
                        setFilter(option);
                        setShowFilterDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm"
                      style={filter === option
                        ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                        : { color: 'var(--color-text-secondary)' }}
                    >
                      {option === 'all' ? 'All Alerts' : option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            onClick={fetchAlerts}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--color-text-dim)' }}
            title="Refresh alerts"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Alert list */}
      <div className={`divide-y ${compact ? 'max-h-64' : 'max-h-96'} overflow-y-auto`} style={{ borderColor: 'var(--color-border-faint)' }}>
        {filteredAlerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="w-10 h-10 mb-2" style={{ color: 'var(--color-verified)' }} />
            <p className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>No alerts</p>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>You're all caught up!</p>
          </div>
        ) : (
          filteredAlerts.map(alert => {
            const config = SEVERITY_CONFIG[alert.severity];
            const Icon = config.icon;

            return (
              <div
                key={alert.id}
                className="p-4 transition-colors"
                style={!alert.read ? config.bg : { backgroundColor: 'var(--color-bg-surface)' }}
                onClick={() => !alert.read && markAsRead(alert.id)}
              >
                <div className="flex items-start">
                  <div className="p-1.5 rounded-lg mr-3 flex-shrink-0" style={config.bg}>
                    <Icon className="w-4 h-4" style={config.iconColor} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium" style={{ color: !alert.read ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
                        {alert.title}
                      </h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissAlert(alert.id);
                        }}
                        className="p-1 rounded transition-colors"
                        style={{ color: 'var(--color-text-dim)' }}
                        title="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{alert.message}</p>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTime(alert.timestamp)}
                        {alert.source && (
                          <span className="ml-2 px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                            {alert.source}
                          </span>
                        )}
                      </div>

                      {alert.actionUrl && (
                        <a
                          href={alert.actionUrl}
                          className="flex items-center text-xs font-medium"
                          style={{ color: 'var(--color-needs-human-text)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {alert.actionLabel || 'View'}
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
